import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import { 
  UserPlus, 
  Search, 
  MessageCircle, 
  CheckCircle, 
  ArrowRight,
  Building2,
  Users,
  Clock,
  Home
} from "lucide-react";

const HowItWorks = () => {
  const navigate = useNavigate();
  const steps = [
    {
      icon: UserPlus,
      number: "01",
      title: "Sign Up & Setup",
      description: "Create your account, verify credentials, and set up your organization profile with care specialties and preferences.",
      userType: "All Users"
    },
    {
      icon: Building2,
      number: "02", 
      title: "Update Availability",
      description: "Providers update real-time bed availability or service capacity through our intuitive dashboard.",
      userType: "Providers"
    },
    {
      icon: Search,
      number: "03",
      title: "Search & Connect",
      description: "Placement professionals search for housing placements or submit HSS service referrals based on client needs.",
      userType: "Placement Pros"
    },
    {
      icon: MessageCircle,
      number: "04",
      title: "Collaborate & Track",
      description: "Secure communication, documentation sharing, and real-time status tracking throughout the placement process.",
      userType: "All Users"
    }
  ];

  const userTypes = [
    {
      icon: Users,
      title: "Case Managers, Social Workers & HSS Workers",
      description: "Find suitable placements quickly for your clients",
      benefits: ["Advanced search filters", "Real-time availability", "Secure messaging"]
    },
    {
      icon: Building2,
      title: "Providers",
      description: "Assisted living, care facilities, and HSS service agencies",
      benefits: ["Easy availability updates", "Direct referral access", "County-based matching", "Analytics dashboard"]
    },
    {
      icon: Clock,
      title: "Discharge Planners",
      description: "Accelerate discharge planning with instant connections",
      benefits: ["Urgent placement alerts", "Multi-facility search", "Status tracking"]
    },
  ];

  return (
    <section id="how-it-works" className="py-24 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-background to-secondary/10">
      <div className="container mx-auto">
        {/* Header */}
        <div className="text-center space-y-4 mb-16">
          <Badge variant="secondary" className="text-sm font-medium">
            How It Works
          </Badge>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold">
            Simple Process,
            <span className="bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
              {" "}Powerful Results
            </span>
          </h2>
           <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
             CareKonnect streamlines the entire placement process for placement professionals 
             and providers, from initial search to successful placement, saving time for everyone involved.
           </p>
        </div>

        {/* Process Steps */}
        <div className="relative mb-24">
          {/* Connection Line */}
          <div className="hidden lg:block absolute top-16 left-0 right-0 h-0.5 bg-gradient-to-r from-primary/20 via-accent/40 to-primary/20 z-0"></div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 relative z-10">
            {steps.map((step, index) => (
              <Card key={index} className="text-center group hover:shadow-lg transition-all duration-300">
                <CardHeader className="space-y-4">
                  <div className="mx-auto relative">
                    <div className="w-16 h-16 bg-gradient-to-br from-primary to-accent rounded-2xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                      <step.icon className="w-8 h-8 text-primary-foreground" />
                    </div>
                    <div className="absolute -top-2 -right-2 w-6 h-6 bg-secondary rounded-full flex items-center justify-center text-xs font-bold text-secondary-foreground">
                      {step.number}
                    </div>
                  </div>
                  <div>
                    <Badge variant="outline" className="text-xs mb-2">
                      {step.userType}
                    </Badge>
                    <CardTitle className="text-lg">{step.title}</CardTitle>
                  </div>
                </CardHeader>
                <CardContent>
                  <CardDescription className="text-sm leading-relaxed">
                    {step.description}
                  </CardDescription>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* User Types */}
        <div className="space-y-8">
          <div className="text-center">
            <h3 className="text-2xl sm:text-3xl font-bold mb-4">
              Designed for Every Role
            </h3>
             <p className="text-muted-foreground max-w-2xl mx-auto">
               Whether you're a placement professional finding care for clients or 
               a provider offering services, CareKonnect adapts to your specific workflow and needs.
             </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {userTypes.map((type, index) => (
              <Card key={index} className="group hover:shadow-lg transition-all duration-300">
                <CardHeader className="space-y-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-primary/10 to-accent/10 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                    <type.icon className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <CardTitle className="text-xl group-hover:text-primary transition-colors">
                      {type.title}
                    </CardTitle>
                    <CardDescription className="text-base">
                      {type.description}
                    </CardDescription>
                  </div>
                </CardHeader>
                <CardContent className="space-y-3">
                  {type.benefits.map((benefit, benefitIndex) => (
                    <div key={benefitIndex} className="flex items-center space-x-3">
                      <CheckCircle className="w-4 h-4 text-success flex-shrink-0" />
                      <span className="text-sm text-muted-foreground">{benefit}</span>
                    </div>
                  ))}
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* CTA */}
        <div className="mt-16 text-center">
          <div className="bg-gradient-to-r from-primary/5 to-accent/5 rounded-2xl p-8">
            <h3 className="text-2xl font-bold mb-4">
              Ready to Transform Your Placement Process?
            </h3>
             <p className="text-muted-foreground mb-6 max-w-2xl mx-auto">
               Join hundreds of providers and placement professionals already using 
               CareKonnect to improve outcomes and reduce placement times.
             </p>
            <Button 
              size="lg" 
              className="bg-gradient-to-r from-primary to-primary-glow hover:shadow-lg"
              onClick={() => navigate('/login')}
            >
              Sign Up Now
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HowItWorks;